import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControlLabel,
	Grid,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { getPgScheduleRequest, updatePgScheduleRequest } from 'redux/reducers/pgSettings/reducer';
import {
	selectPgSaveLoading,
	selectPgSchedule,
	selectPgScheduleLoading,
} from 'redux/reducers/pgSettings/selectors';
import { TPgScheduleSettings, TPgScheduleSettingsRequest } from 'services/api/paymentGateway/types';
import { PgPanel } from '../common/PgPanel';
import { formatDateTime } from '../utils';

type TNumberField =
	| 'runHourUtc'
	| 'blackoutFromDay'
	| 'blackoutToDay'
	| 'ddDay'
	| 'ddCatchUpDays'
	| 'ddFirstDebitDelayBusinessDays'
	| 'runMissedAlertHours'
	| 'cardFirstDay'
	| 'cardAttemptDays';

type TFlagField =
	| 'enabled'
	| 'ddEnabled'
	| 'cardsEnabled'
	| 'fallbackToCardsAfterDdFailure'
	| 'skipDisabledCustomers'
	| 'notifyOnFailure';

type TScheduleFormValues = Record<TNumberField, string> &
	Record<TFlagField, boolean> & { onlyInvoicesCreatedAfter: string };

type TScheduleErrors = Partial<Record<keyof TScheduleFormValues, string>>;

/** Limits of `schedule_settings` (02 §2.14), checked again by the gateway. */
const RANGES: Record<TNumberField, [number, number]> = {
	runHourUtc: [0, 23],
	blackoutFromDay: [1, 28],
	blackoutToDay: [1, 28],
	ddDay: [1, 28],
	ddCatchUpDays: [0, 10],
	ddFirstDebitDelayBusinessDays: [0, 10],
	runMissedAlertHours: [0, 23],
	cardFirstDay: [1, 28],
	cardAttemptDays: [1, 20],
};

const NUMBER_FIELDS = Object.keys(RANGES) as TNumberField[];
const FLAG_FIELDS: TFlagField[] = [
	'enabled',
	'ddEnabled',
	'cardsEnabled',
	'fallbackToCardsAfterDdFailure',
	'skipDisabledCustomers',
	'notifyOnFailure',
];

const toValues = (s: TPgScheduleSettings): TScheduleFormValues => ({
	...(Object.fromEntries(NUMBER_FIELDS.map((f) => [f, String(s[f])])) as Record<
		TNumberField,
		string
	>),
	...(Object.fromEntries(FLAG_FIELDS.map((f) => [f, s[f]])) as Record<TFlagField, boolean>),
	onlyInvoicesCreatedAfter: s.onlyInvoicesCreatedAfter ?? '',
});

const toRequest = (v: TScheduleFormValues): TPgScheduleSettingsRequest => ({
	...(Object.fromEntries(NUMBER_FIELDS.map((f) => [f, Number(v[f])])) as Record<
		TNumberField,
		number
	>),
	...(Object.fromEntries(FLAG_FIELDS.map((f) => [f, v[f]])) as Record<TFlagField, boolean>),
	onlyInvoicesCreatedAfter: v.onlyInvoicesCreatedAfter || null,
});

const validate = (v: TScheduleFormValues): TScheduleErrors => {
	const errors: TScheduleErrors = {};
	NUMBER_FIELDS.forEach((field) => {
		const [min, max] = RANGES[field];
		const text = v[field].trim();
		const value = Number(text);
		if (!/^\d+$/.test(text) || value < min || value > max) errors[field] = `${min}–${max}`;
	});
	const n = (field: TNumberField) => Number(v[field]);
	if (!errors.blackoutFromDay && !errors.blackoutToDay && n('blackoutToDay') < n('blackoutFromDay'))
		errors.blackoutToDay = 'Must be ≥ the first blackout day';
	if (!errors.ddDay && !errors.ddCatchUpDays && n('ddDay') + n('ddCatchUpDays') > 28)
		errors.ddCatchUpDays = 'Direct Debit day + catch-up days must not exceed 28';
	if (
		!errors.cardFirstDay &&
		!errors.cardAttemptDays &&
		n('cardFirstDay') + n('cardAttemptDays') - 1 > 28
	)
		errors.cardAttemptDays = 'The last attempt day must not exceed 28';
	return errors;
};

const pad = (hour: string) => `${hour.padStart(2, '0')}:00 UTC`;

/** Human-readable summary of the values on screen. */
const describe = (v: TScheduleFormValues): string[] => {
	if (!v.enabled) return ['The monthly cycle is switched off: nothing is charged automatically.'];
	const lines = [
		`Daily run at ${pad(v.runHourUtc)}; no charges on days ${v.blackoutFromDay}–${v.blackoutToDay} of the month.`,
	];
	const lastCardDay = Number(v.cardFirstDay) + Number(v.cardAttemptDays) - 1;
	lines.push(
		v.ddEnabled
			? `Direct Debit (UK customers): one request on day ${v.ddDay}${Number(v.ddCatchUpDays) ? `, catch-up until day ${Number(v.ddDay) + Number(v.ddCatchUpDays)} if missed` : ''}.`
			: 'Direct Debit is not requested by the cycle.',
	);
	lines.push(
		v.cardsEnabled
			? `Cards and wallets: attempts on days ${v.cardFirstDay}–${lastCardDay} (${v.cardAttemptDays} day${v.cardAttemptDays === '1' ? '' : 's'}).`
			: 'Cards and wallets are not charged by the cycle.',
	);
	return lines;
};

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
	<Grid item xs={12} md={4}>
		<Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
			{title}
		</Typography>
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>{children}</Box>
	</Grid>
);

/** Settings of the monthly charging cycle (decision 13, 03 §4.6). */
export const ScheduleForm: FC = () => {
	const dispatch = useDispatch();
	const schedule = useSelector(selectPgSchedule);
	const loading = useSelector(selectPgScheduleLoading);
	const saving = useSelector(selectPgSaveLoading('schedule'));
	const [values, setValues] = useState<TScheduleFormValues | null>(null);

	useEffect(() => {
		dispatch(getPgScheduleRequest());
	}, [dispatch]);

	// Every new settings object (loaded or returned by a save) resets the form.
	const [syncedFrom, setSyncedFrom] = useState<typeof schedule | undefined>(undefined);
	if (schedule !== syncedFrom) {
		setSyncedFrom(schedule);
		setValues(schedule ? toValues(schedule) : null);
	}

	const errors = useMemo(() => (values ? validate(values) : {}), [values]);
	const hasErrors = Object.keys(errors).length > 0;
	const changed = useMemo(() => {
		if (!values || !schedule) return false;
		const saved = toValues(schedule);
		return (Object.keys(saved) as (keyof TScheduleFormValues)[]).some(
			(key) => saved[key] !== values[key],
		);
	}, [values, schedule]);

	const setField = <K extends keyof TScheduleFormValues>(key: K, value: TScheduleFormValues[K]) =>
		setValues((v) => (v ? { ...v, [key]: value } : v));

	const numberField = (field: TNumberField, label: string, helperText?: string) =>
		values && (
			<TextField
				size="small"
				label={label}
				value={values[field]}
				onChange={(e) => setField(field, e.target.value)}
				error={!!errors[field]}
				helperText={errors[field] ?? helperText ?? `${RANGES[field][0]}–${RANGES[field][1]}`}
				inputProps={{ inputMode: 'numeric' }}
			/>
		);

	const flagField = (field: TFlagField, label: string) =>
		values && (
			<FormControlLabel
				control={
					<Switch checked={values[field]} onChange={(e) => setField(field, e.target.checked)} />
				}
				label={label}
			/>
		);

	const handleSave = () => {
		if (!values || hasErrors) return;
		dispatch(updatePgScheduleRequest(toRequest(values)));
	};

	return (
		<PgPanel
			title="Monthly cycle"
			actions={
				<>
					{(loading || saving) && <CircularProgress size={18} />}
					<Button
						size="small"
						onClick={() => schedule && setValues(toValues(schedule))}
						disabled={!changed || saving}
					>
						Discard
					</Button>
					<Button
						size="small"
						variant="contained"
						onClick={handleSave}
						disabled={!changed || hasErrors || saving}
					>
						Save
					</Button>
				</>
			}
		>
			{!values || !schedule ? (
				<Typography variant="body2" color="text.secondary">
					{loading ? 'Loading…' : 'No data.'}
				</Typography>
			) : (
				<>
					<Alert severity={values.enabled ? 'info' : 'warning'} sx={{ mb: 2 }}>
						{describe(values).map((line) => (
							<div key={line}>{line}</div>
						))}
						<div>
							Every unpaid invoice (balance &gt; 0) allowed by the debt transfer rules is charged
							with the payment methods bound to its service; Direct Debit goes first for UK
							customers.
						</div>
					</Alert>
					<Grid container spacing={3}>
						<Section title="General">
							{flagField('enabled', 'Monthly cycle enabled')}
							{numberField('runHourUtc', 'Run hour (UTC)', '0–23, daily run time')}
							{numberField('blackoutFromDay', 'Blackout from day', 'No charges from this day')}
							{numberField('blackoutToDay', 'Blackout to day', 'Up to this day, inclusive')}
							{numberField(
								'runMissedAlertHours',
								'Missed run alert after, hours',
								'Alert when the daily run did not happen',
							)}
							<TextField
								size="small"
								type="date"
								label="Only invoices created after"
								value={values.onlyInvoicesCreatedAfter}
								onChange={(e) => setField('onlyInvoicesCreatedAfter', e.target.value)}
								InputLabelProps={{ shrink: true }}
								helperText="Safety net: older invoices are never charged automatically; empty — off"
							/>
							{flagField('skipDisabledCustomers', 'Skip customers disabled in EAS')}
							{flagField('notifyOnFailure', 'E-mail the customer once per invoice on failure')}
						</Section>
						<Section title="Direct Debit">
							{flagField('ddEnabled', 'Request Direct Debit')}
							{numberField('ddDay', 'Direct Debit day', 'One request per month on this day')}
							{numberField(
								'ddCatchUpDays',
								'Catch-up days',
								'0–10: the request may still be made this many days later',
							)}
							{numberField(
								'ddFirstDebitDelayBusinessDays',
								'First debit delay, business days',
								'0–10: wait after a new mandate before the first debit',
							)}
							{flagField(
								'fallbackToCardsAfterDdFailure',
								'Fall back to cards after a failed Direct Debit',
							)}
						</Section>
						<Section title="Cards and wallets">
							{flagField('cardsEnabled', 'Charge cards and wallets')}
							{numberField('cardFirstDay', 'First attempt day')}
							{numberField('cardAttemptDays', 'Attempt days', '1–20 consecutive days')}
						</Section>
					</Grid>
					<Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
						Last change: {formatDateTime(schedule.updatedAt)}
						{schedule.updatedBy ? ` by ${schedule.updatedBy}` : ''}
					</Typography>
				</>
			)}
		</PgPanel>
	);
};
